/**
 * The violation materials: what an impact is made of, once the topology has decided
 * where it is.
 *
 * Three materials per effect and no more — one merged ground surface, one volume,
 * one particle domain per connected component. The branch inside each is chosen by
 * `uMode`, which is the violation and nothing else.
 *
 * The visual language each branch is trying to speak (spec §8): Physical is
 * displacement and dust, not coloured magic. Heat is convection and distortion, not
 * orange dots. Cold grows crystal rather than tinting things blue. Darkness absorbs
 * the board's own light through its material instead of asking for a negative lamp,
 * which does not exist. Mental refracts and drifts out of phase. None of them
 * depends on colour alone to be recognisable.
 */

import {
  AdditiveBlending,
  Color,
  DoubleSide,
  NormalBlending,
  ShaderMaterial,
  Vector2,
} from 'three';

import {
  EMISSION_CHUNK,
  IDEAL_SHAPE_CHUNK,
  INTENT_CHUNK,
  LIFECYCLE_CHUNK,
  NOISE_CHUNK,
  PARTICLE_PATH_CHUNK,
  PROJECTION_CHUNK,
  projectionOptions,
} from './shaders';
import { FOOTPRINT_IDS, MERGE_IDS, violationProfile } from './violation';

function blendingFor(violation) {
  return violationProfile(violation).additive ? AdditiveBlending : NormalBlending;
}

/** The ground and the light above it are not the same job — see VIOLATION_PROFILES. */
export function decalBlendingFor(violation) {
  return violationProfile(violation).decalAdditive ? AdditiveBlending : NormalBlending;
}

function emissionUniforms(profile) {
  return {
    uColorA: { value: new Color(profile.color) },
    uColorB: { value: new Color(profile.deep) },
    uColorCore: { value: new Color(profile.core || '#ffffff') },
  };
}

/**
 * How the debris off one violation actually flies.
 *
 * The numbers are the physics, not the palette: a stone impact throws heavy grit
 * hard and low and it is down in a third of a second; fire lets light material go
 * and it rises for a second and a half; frost barely moves at all. Getting these
 * wrong is what makes every effect in a game look like the same puff of dust wearing
 * a different colour.
 *
 *   speed    initial speed along the impact direction, cells/s
 *   spread   how much of that speed is randomised into a hemisphere
 *   rise     the vertical component, cells/s
 *   gravity  cells/s², downward
 *   drag     linear drag constant; high drag means it stops dead
 *
 * Drag is also the leash. A cloud that carries two cells past the footprint tells
 * the player the blast reached ground it never touched, so the asymptotic travel
 * (speed / drag) is kept near one cell for every violation.
 */
const BURST_STYLES = {
  None: { speed: 1.4, spread: 1.1, rise: 1.3, gravity: 1.4, drag: 3.2, life: [0.5, 0.9], size: [0.05, 0.1] },
  Physical: { speed: 3.6, spread: 2.0, rise: 2.4, gravity: 7.5, drag: 2.6, life: [0.3, 0.75], size: [0.03, 0.09] },
  Mental: { speed: 1.2, spread: 1.6, rise: 1.0, gravity: -0.6, drag: 2.8, life: [0.7, 1.3], size: [0.05, 0.12] },
  Energy: { speed: 4.4, spread: 2.4, rise: 2.0, gravity: 3.2, drag: 5.0, life: [0.22, 0.55], size: [0.03, 0.08] },
  Heat: { speed: 2.2, spread: 1.6, rise: 3.0, gravity: -1.4, drag: 3.2, life: [0.6, 1.4], size: [0.05, 0.13] },
  Cold: { speed: 1.3, spread: 1.3, rise: 1.1, gravity: 1.8, drag: 3.8, life: [0.6, 1.2], size: [0.04, 0.1] },
  Light: { speed: 1.7, spread: 1.2, rise: 2.6, gravity: -0.9, drag: 3.0, life: [0.7, 1.3], size: [0.05, 0.12] },
  Darkness: { speed: 1.9, spread: 1.8, rise: 1.2, gravity: 2.2, drag: 3.4, life: [0.6, 1.2], size: [0.06, 0.14] },
};

export function burstStyle(violation) {
  return BURST_STYLES[violation] || BURST_STYLES.None;
}

/**
 * What the affected ground keeps doing after the strike.
 *
 *   rate     how fast one mote lives out its loop
 *   rise     how far it climbs over that life, in cells
 *   swirl    lateral wander, because motes going straight up read as a fountain
 *   twinkle  how much of its brightness is flicker rather than fade
 */
const AMBIENT_MOTION = {
  None: { rate: 0.18, rise: 0.55, swirl: 0.14, twinkle: 0.25 },
  Physical: { rate: 0.5, rise: 0.3, swirl: 0.05, twinkle: 0.15 },
  Mental: { rate: 0.13, rise: 0.5, swirl: 0.3, twinkle: 0.5 },
  Energy: { rate: 0.85, rise: 0.4, swirl: 0.06, twinkle: 1.0 },
  Heat: { rate: 0.42, rise: 1.7, swirl: 0.2, twinkle: 0.3 },
  Cold: { rate: 0.1, rise: 0.28, swirl: 0.1, twinkle: 0.8 },
  Light: { rate: 0.22, rise: 1.05, swirl: 0.12, twinkle: 0.4 },
  Darkness: { rate: 0.28, rise: 0.6, swirl: 0.24, twinkle: 0.2 },
};

export function ambientMotion(violation) {
  return AMBIENT_MOTION[violation] || AMBIENT_MOTION.None;
}

function shapeUniforms(hint, params) {
  return {
    uFootprint: { value: FOOTPRINT_IDS[hint] ?? 5 },
    uOrigin: { value: new Vector2(params.origin.x, params.origin.z) },
    uDirection: { value: new Vector2(params.direction.x, params.direction.z) },
    uRadius: { value: params.radius },
    uHalfWidth: { value: params.halfWidth },
    uLength: { value: params.length },
    uHalfAngle: { value: params.halfAngle },
    uReach: { value: params.reach },
  };
}

export function createDecalMaterial({ shared, component, violation, path = null, hint, mergeMode, params, sdfTexture }) {
  const profile = violationProfile(violation, path);
  const { rect } = component.sdf;

  const material = new ShaderMaterial({
    uniforms: {
      ...shared,
      ...shapeUniforms(hint, params),
      uSDF: { value: sdfTexture },
      uSdfMin: { value: new Vector2(rect.x, rect.z) },
      uSdfSize: { value: new Vector2(rect.width, rect.height) },
      uMode: { value: profile.mode },
      uMerge: { value: MERGE_IDS[mergeMode] ?? 2 },
      /*
       * The field already has no corners, so nothing here has to take any off. The
       * nine-tap blur below exists purely to round a union of squares, and run over a
       * union of discs it does the one thing it must never do: it shrinks. On a
       * single cell it subtracts more than half the disc's own radius, which is the
       * difference between a lit patch of floor and nothing at all.
       */
      uRadialSdf: { value: component.sdf.radial ? 1 : 0 },
      uEdgeNoise: { value: mergeMode === 'strict-cells' ? 0 : profile.edgeNoise },
      ...emissionUniforms(profile),
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      varying vec2 vGrid;
      void main(){
        vec3 p = gridPosition(position);
        vGrid = p.xz;
        gl_Position = gridToClip(p);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vGrid;
      uniform sampler2D uSDF;
      uniform vec2 uSdfMin;
      uniform vec2 uSdfSize;
      uniform int uMode;
      uniform int uMerge;
      uniform int uRadialSdf;
      uniform float uEdgeNoise;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      ${INTENT_CHUNK}
      ${NOISE_CHUNK}
      ${IDEAL_SHAPE_CHUNK}

      float cellsSD(vec2 p){
        vec2 uv = (p - uSdfMin) / uSdfSize;
        return (texture2D(uSDF, uv).r * 2.0 - 1.0) * 3.0;
      }

      /*
       * The cell union with its corners taken off.
       *
       * Blurring the distance field and thresholding it slightly inside rounds every
       * convex corner and thins the sharp notches where two cells meet at a point —
       * the two things that make a merged area read as squares. The bias is what
       * guarantees the result stays INSIDE the authoritative union: the blur can pull
       * the boundary in, and is not permitted to push it out.
       */
      float roundedCellsSD(vec2 p, float radius){
        float total = cellsSD(p);
        total += cellsSD(p + vec2(radius, 0.0));
        total += cellsSD(p + vec2(-radius, 0.0));
        total += cellsSD(p + vec2(0.0, radius));
        total += cellsSD(p + vec2(0.0, -radius));
        float diagonal = radius * 0.7071;
        total += cellsSD(p + vec2(diagonal, diagonal));
        total += cellsSD(p + vec2(-diagonal, diagonal));
        total += cellsSD(p + vec2(diagonal, -diagonal));
        total += cellsSD(p + vec2(-diagonal, -diagonal));
        return total / 9.0 - radius;
      }

      void main(){
        vec2 world = vGrid;
        vec2 p = world + uSeed * 13.7;

        float fromCells = (uMerge == 3 && uRadialSdf == 0) ? roundedCellsSD(world, 0.34) : cellsSD(world);
        float fromIdeal = (uFootprint == 5) ? fromCells : idealSD(world);
        /*
         * Ground does not burn in a circle. The ideal boundary is pushed in and out
         * by the same field the surface is made of, so the effect ends where the
         * material ran out rather than where the maths did — and because the wander
         * is applied to the ideal side only, the min with the cells still holds: a
         * clipped effect can lose ground to the noise but can never gain any.
         */
        fromIdeal += (fbm(world * 1.35 + uSeed * 2.1) - 0.5) * uEdgeNoise;
        float mask = (uMerge == 0 || uMerge == 3) ? fromCells
                   : (uMerge == 1 ? fromIdeal : min(fromCells, fromIdeal));
        /*
         * Rounded ground gets the edge wander too, but inward only: the boundary may
         * eat into the claimed area, never past it. Strict stays untouched — its whole
         * job is to be checkable.
         */
        if (uMerge == 3) mask -= abs(fbm(world * 1.6 + uSeed * 1.7) - 0.5) * uEdgeNoise * (uRadialSdf == 1 ? 0.45 : 1.0);

        /*
         * The boundary is FELT, never stroked.
         *
         * Every version of this that read as programmer art did the same thing: it
         * took the distance to the edge and drew a line there. So there is no edge
         * term here at all. What there is instead is a wide feather inward from the
         * boundary and a radial falloff from the origin, multiplied — which is what
         * a lamp lying on uneven ground actually looks like, brightest under itself
         * and dying out before it reaches anything it does not touch.
         */
        /*
         * A radial field's whole positive range is one cell radius, so thresholding it
         * over 0.85 of a cell leaves even the centre of a lone hit under a fifth
         * strength. Feathered over the radius it actually has, the disc reads full in
         * the middle and dies at its own rim — which is the shape, not a fade of it.
         */
        float feather = uRadialSdf == 1 ? 0.5 : (uMerge == 3 ? 0.85 : 0.7);
        float shape = smoothstep(0.0, feather, mask);
        if (shape < 0.003) discard;
        float toOrigin = length(world - uOrigin);
        float core = pow(max(0.0, 1.0 - toOrigin / max(uReach * 1.25, 0.001)), 2.1);
        /*
         * The pool is PURELY radial, with no flat floor under it.
         *
         * A constant term multiplied by the cell mask lights every square of the
         * footprint evenly, and on a single-cell hit that is literally a lit square
         * drawn on the board — the exact artifact this rebuild exists to remove. All
         * the brightness has to come from the falloff, so the boundary is reached by
         * the light dying rather than by the mask cutting it off.
         */
        float pool = shape * core * (0.5 + 0.85 * core);

        float t = uTime;
        /* The moment of arrival, on its own clock: a hard bloom that decays fast. */
        float flash = exp(-uSince * 6.5) * step(0.001, uSince);
        float n = fbm(p * 1.8);

        vec3 color = uColorA;
        float alpha = 0.0;
        float heat = 0.0;

        if (uMode == 0){
          /* Phosphor: a slow luminous breathing with no structure of its own. */
          float lum = 0.35 + 0.65 * fbm(p * 1.1 + vec2(t * 0.12, -t * 0.09));
          heat = core * lum + flash * 0.9;
          alpha = pool * (0.2 + 0.42 * lum);
        } else if (uMode == 1){
          /*
           * Grit. The ground is DARKENED — this is the one material drawn with a
           * normal blend — and the only light in it is the flash of the strike and a
           * few flecks still glowing in the dust.
           */
          float dust = fbm(p * 2.3 + vec2(0.0, t * 0.05));
          float scorch = smoothstep(0.15, 0.95, dust) * (0.5 + 0.5 * uChar);
          color = mix(uColorB, uColorB * 1.8, dust);
          heat = flash * 1.2 + embers(p * 5.0, t * 0.5, 0.16) * core * 0.5;
          color = mix(color, emberRamp(0.7 + flash), min(1.0, heat));
          alpha = pool * (0.24 + 0.42 * scorch) + heat * shape * 0.55;
        } else if (uMode == 2){
          /* Refraction: colour pulled apart along the noise gradient, drifting. */
          vec2 warp = vec2(fbm(p * 1.4 + t * 0.13), fbm(p * 1.4 + 5.2 - t * 0.11)) - 0.5;
          float lum = fbm(p * 1.7 + warp * 2.4);
          color = mix(uColorB, uColorA, smoothstep(0.25, 0.8, lum));
          color += uColorCore * pow(max(0.0, lum - 0.55), 2.0) * 1.6;
          heat = core * lum * 0.8 + flash * 0.8;
          alpha = pool * (0.16 + 0.5 * lum);
        } else if (uMode == 3){
          /* Filaments: bright thin discharge paths crawling over the lit ground. */
          float vein = pow(1.0 - abs(fbm(p * 2.2 + vec2(t * 0.45, t * 0.15)) * 2.0 - 1.0), 8.0);
          heat = vein * 1.3 + core * 0.35 + flash;
          alpha = pool * 0.3 + vein * shape * 0.9 + flash * shape * 0.5;
          alpha *= 0.85 + 0.3 * smoothstep(0.86, 1.0, fract(t * 2.1 + n));
        } else if (uMode == 4){
          /* Convection, rising off the surface, cooling to scorch as it settles. */
          float glow = fbm(p * 2.0 + vec2(0.0, -t * 0.85) + n * 1.1);
          float hot = smoothstep(0.28, 0.95, glow);
          float scorch = uChar * smoothstep(0.15, 1.0, mask / 1.4) * (0.45 + 0.45 * fbm(p * 3.1));
          heat = hot * (0.55 + 0.6 * core) + flash * 1.2
               + embers(p * 3.2 + vec2(0.0, -t * 0.6), t * 0.9, 0.26) * 0.9;
          alpha = pool * (0.25 + 0.6 * hot);
          color = mix(emberRamp(heat), vec3(0.05, 0.035, 0.03), scorch * 0.6);
          alpha *= 1.0 - scorch * 0.25;
          heat = -1.0;
        } else if (uMode == 5){
          /* A crystal front, growing outward from the strike and holding. */
          float crystal = 1.0 - smoothstep(0.0, 0.15, voronoiEdge(p * 2.6));
          float front = smoothstep(uGrowth * uReach + 0.5, uGrowth * uReach - 0.3, toOrigin);
          heat = crystal * 0.85 + flash + core * 0.3;
          alpha = pool * front * (0.22 + 0.55 * crystal);
        } else if (uMode == 6){
          /* Light does nothing clever: it is bright in the middle and it falls off. */
          float lum = 0.55 + 0.45 * fbm(p * 1.3 + t * 0.18);
          heat = core * lum * 1.3 + flash * 1.1;
          alpha = pool * (0.22 + 0.34 * lum);
        } else {
          /*
           * Darkness takes the board's light away, so its body is nearly black and
           * everything readable about it is the violet burning just inside its own
           * boundary — a wide, noisy, unstroked rim, not a contour line.
           */
          vec2 inward = normalize(uOrigin - world + vec2(0.0001));
          float advection = fbm(p * 1.4 + inward * t * 0.45);
          float rim = smoothstep(1.5, 0.15, mask) * (0.45 + 0.55 * advection);
          color = mix(vec3(0.015, 0.01, 0.03), uColorA * 0.9, rim * 0.75);
          color += uColorCore * flash * 0.8;
          alpha = pool * (0.55 + 0.3 * advection) + rim * shape * 0.35;
          heat = -1.0;
        }

        /* Modes that did not build their own colour take the emission ramp. */
        if (heat >= 0.0) color = emberRamp(heat);

        if (uFootprint == 1){
          float along = clamp(dot(world - uOrigin, uDirection) / max(uLength, 0.001), 0.0, 1.0);
          alpha *= 0.45 + 0.75 * along;
          alpha += exp(-pow(fract(t * 0.55) - along, 2.0) * 60.0) * 0.5 * shape;
        }

        /*
         * What the action is FOR, laid over what it is made of.
         *
         * Composed rather than branched: the violation has already decided the
         * material, and this adds the motion and the marks that say whether it is
         * being given or taken. A Heat ward stays hot and gains plating; a Heat
         * curse stays hot and gains creep.
         */
        if (uIntent == 1){
          // Mend: light drawn IN toward the subject. A gradient that tightens over
          // time, never a set of concentric rings — rings are the tell of a decal.
          float gather = pow(clamp(1.0 - toOrigin / max(uReach, 0.001), 0.0, 1.0), 1.4);
          gather *= 0.55 + 0.45 * fbm(p * 1.6 - vec2(0.0, t * 0.7));
          gather *= smoothstep(0.0, 0.6, mask);
          color = mix(color, uIntentBias, gather * 0.45);
          alpha += gather * 0.3 * shape;
        } else if (uIntent == 2){
          // Bolster: a plated floor. Structure is the whole statement — something
          // has been ADDED here, and added things have joins.
          float plating = 1.0 - smoothstep(0.0, 0.1, voronoiEdge(p * 1.9));
          color = mix(color, uIntentBias, plating * 0.5);
          alpha += plating * 0.22 * shape * (0.4 + core);
        } else if (uIntent == 3){
          // Afflict: creep running inward, and the ground losing its colour to it.
          vec2 inward = normalize(uOrigin - world + vec2(0.0001));
          float creep = pow(1.0 - abs(fbm(p * 2.4 + inward * t * 0.5) * 2.0 - 1.0), 5.0);
          color = mix(color, uIntentBias, creep * 0.6);
          color *= 1.0 - creep * 0.35;
          alpha += creep * 0.3 * shape;
        } else if (uIntent == 5){
          // Guard: a lattice that thickens toward the rim, because a ward is a thing
          // held between its subject and everything else.
          float lattice = 1.0 - smoothstep(0.0, 0.08, voronoiEdge(p * 2.4));
          float rimward = smoothstep(0.9, 0.05, mask);
          color = mix(color, uIntentBias, 0.35 + lattice * 0.35);
          alpha += (lattice * 0.16 + rimward * 0.26) * shape;
        } else if (uIntent == 4){
          // Reveal: one WAVE going out, soft enough that its front is felt rather
          // than seen, and nothing left behind it.
          float sweep = fract(t * 0.42);
          float scan = exp(-pow(toOrigin - sweep * uReach, 2.0) * 1.1);
          color = mix(color, uIntentBias, scan * 0.6);
          alpha += scan * 0.3 * shape;
        }

        color = intentTint(color);
        alpha *= 0.6 + 0.7 * core;
        alpha *= uActivation * (0.35 + 0.85 * uIntensity);
        /*
         * The board underneath stays legible — but the cap follows the light rather
         * than being flat. A single ceiling everywhere is what flattens an effect
         * into a sticker: the middle of a blast is allowed to blow out, and the
         * ground two cells away is not allowed to be touched at all.
         */
        alpha = min(alpha, 0.28 + 0.62 * core);
        float threshold = fbm(p * 2.7 + 3.3);
        alpha *= 1.0 - smoothstep(threshold - 0.18, threshold + 0.12, uDissolve * 1.25);

        gl_FragColor = vec4(shadowed(color, uColorB), clamp(alpha, 0.0, 1.0));
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
  });

  material.blending = decalBlendingFor(violation);
  return material;
}

export function createVolumeMaterial({ shared, violation, path = null, height, component, sdfTexture, clip = true, dome = false, spectrum = [] }) {
  const profile = violationProfile(violation, path);
  const rect = component ? component.sdf.rect : { x: 0, z: 0, width: 1, height: 1 };
  /* Always seven slots: a GLSL array has to be a fixed size, and an unused tail
     costs nothing because `uSpectrum` is what decides how many are read. */
  const spectrumColors = Array.from({ length: 7 }, (unused, index) => (
    new Color(spectrum[index] ? violationProfile(spectrum[index]).color : profile.color)
  ));

  const material = new ShaderMaterial({
    uniforms: {
      ...shared,
      uMode: { value: profile.mode },
      ...emissionUniforms(profile),
      uDome: { value: dome ? 1 : 0 },
      uSpectrum: { value: Math.min(7, spectrum.length) },
      uSpectrumColors: { value: spectrumColors },
      uVolumeHeight: { value: height },
      uSDF: { value: sdfTexture || null },
      uSdfMin: { value: new Vector2(rect.x, rect.z) },
      uSdfSize: { value: new Vector2(rect.width, rect.height) },
      uClip: { value: clip && sdfTexture ? 1 : 0 },
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      uniform int uMode;
      uniform int uDome;
      uniform float uTime;
      uniform float uGrowth;
      varying vec2 vGrid;
      varying float vHeight;
      varying vec3 vNormal2;
      void main(){
        vec3 local = position;
        if (uMode == 2){
          local += normal * sin(uTime * 2.6 + position.x * 4.0 + position.y * 3.0 + position.z * 2.0) * 0.06;
        }
        /*
         * A ward INFLATES. It overshoots its resting size and settles back, because
         * a shield that fades up to full size reads as an opacity animation, and one
         * that snaps out and rebounds reads as something being deployed.
         */
        if (uDome == 1){
          float g = clamp(uGrowth, 0.0, 1.0);
          float inflate = 1.0 - pow(1.0 - g, 3.0);
          inflate += sin(g * 9.0) * (1.0 - g) * 0.18;
          local *= max(inflate, 0.02);
        }
        vec3 p = gridPosition(local);
        vGrid = p.xz;
        vHeight = p.y;
        vNormal2 = normalize(mat3(modelMatrix) * normal);
        gl_Position = gridToClip(p);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vGrid;
      varying float vHeight;
      varying vec3 vNormal2;
      uniform int uMode;
      uniform int uDome;
      uniform int uSpectrum;
      uniform vec3 uSpectrumColors[7];
      uniform float uVolumeHeight;
      uniform sampler2D uSDF;
      uniform vec2 uSdfMin;
      uniform vec2 uSdfSize;
      uniform int uClip;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      ${INTENT_CHUNK}
      ${NOISE_CHUNK}

      void main(){
        /*
         * A volume is trimmed against the same authoritative cells its surface is,
         * on the ground directly beneath it. The ideal body is allowed to be rounder
         * than the cell union — that is the point of it — but it is never allowed to
         * put a lit mass over ground the impact did not touch.
         */
        float clipped = 1.0;
        if (uClip == 1){
          vec2 uv = (vGrid - uSdfMin) / uSdfSize;
          float sd = (texture2D(uSDF, uv).r * 2.0 - 1.0) * 3.0;
          clipped = smoothstep(-0.12, 0.34, sd);
          if (clipped < 0.004) discard;
        }
        float t = uTime;
        /*
         * There is no camera to take a view vector from — the board's projection is
         * bilinear, not perspective. A rim term from how edge-on the surface is
         * standing gives the same read: the silhouette of a volume glows and its
         * face stays quiet.
         */
        float rim = pow(1.0 - abs(vNormal2.y), 2.1);
        float height = clamp(vHeight / max(uVolumeHeight, 0.001), 0.0, 1.0);
        float body = 0.5;
        vec3 color = uColorA;

        if (uMode == 4){
          body = smoothstep(0.3, 0.95, fbm(vec2(vGrid.x * 1.6 + vGrid.y * 1.1 + uSeed, vHeight * 2.2 - t * 1.7)));
          body *= 1.0 - height * 0.55;
          color = mix(uColorB, uColorA, body) + vec3(0.9, 0.5, 0.2) * body * 0.4;
        } else if (uMode == 3){
          body = pow(1.0 - abs(fbm(vec2(vHeight * 2.0 - t * 2.2, vGrid.x * 2.0 + vGrid.y * 2.0 + uSeed)) * 2.0 - 1.0), 5.0);
          color = mix(uColorB, uColorA, body) + vec3(body * 0.5);
          body *= 0.7 + 0.5 * smoothstep(0.86, 0.98, fract(t * 2.6 + vHeight));
        } else if (uMode == 5){
          body = 0.22 + 0.45 * (1.0 - smoothstep(0.0, 0.16, voronoiEdge(vGrid * 2.4 + vHeight * 0.8 + uSeed)));
          color = mix(uColorB, uColorA, body);
        } else if (uMode == 7){
          body = fbm(vGrid * 1.2 - t * 0.18) + 0.2;
          body *= 1.15 - height * 0.75;
          color = mix(vec3(0.02, 0.015, 0.05), uColorA * 0.35, rim * 0.6);
        } else if (uMode == 2){
          body = 0.5 + 0.5 * sin(vHeight * 8.0 + t * 2.0 + fbm(vGrid * 2.0 + uSeed) * 6.0);
          color = mix(uColorB, uColorA, body);
        } else if (uMode == 6){
          body = 0.6 + 0.4 * fbm(vGrid * 2.0 + t * 0.3 + uSeed);
          color = mix(uColorA, vec3(1.0, 0.98, 0.9), rim * 0.7);
        } else {
          body = 0.35 + 0.35 * fbm(vGrid * 1.6 + t * 0.25 + uSeed);
          color = mix(uColorB, uColorA, body);
        }

        /*
         * A ward is a made object and should read as one: plated, jointed, and
         * brightest where the plates meet. Everything else keeps the smooth body its
         * violation gave it.
         */
        if (uIntent == 2 || uIntent == 5){
          float joins = 1.0 - smoothstep(0.0, 0.09, voronoiEdge(vec2(vGrid.x + vHeight * 1.4, vGrid.y) * 2.6));
          color = mix(color, uIntentBias, 0.35 + joins * 0.4);
          body = mix(body * 0.7, 1.0, joins * 0.65);
        }

        /*
         * PLATES, NOT A SHELL OF REVOLUTION.
         *
         * The dome used to be read off pow(1 - abs(normal.y), k) and nothing else.
         * That term is constant along every latitude of a hemisphere, so it lit the
         * entire base circle to the same value at once and drew a genuine ellipse of
         * light around the subject's feet — a silhouette rather than a stroke, but
         * indistinguishable from one on a raked board, and the single artifact this
         * renderer exists to refuse. No function of latitude alone can avoid it.
         *
         * So the shell is broken across its AZIMUTH into plates: each dies at its own
         * seams, no two carry the same weight, and they are lit by how far each turns
         * toward the viewer rather than by how edge-on it stands. There is no angle at
         * which they line up into a continuous rim, and what is brightest is the face
         * of the thing standing between the subject and the world — which is what a
         * ward is — instead of its outline.
         */
        if (uDome == 1){
          float azimuth = atan(vNormal2.z, vNormal2.x) / 6.2831853 + 0.5;
          float plateCount = uSpectrum > 0 ? float(uSpectrum) : 9.0;
          float f = azimuth * plateCount + uSeed * 0.31;
          float plateId = floor(f);
          float within = fract(f);
          /* Narrow seams. A plate soft over most of its own span is not a plate; the
             near half of the shell merged into two lobes of fog. */
          float plate = smoothstep(0.0, 0.16, within) * smoothstep(1.0, 0.84, within);
          float weight = 0.5 + 0.9 * hashNoise(vec2(plateId, floor(uSeed * 9.0) + 1.0));

          /*
           * There is no camera to take a view vector from, but the board's projection
           * puts +z nearest the viewer, so a plate's z-normal is how square-on it is.
           * The far half of the shell stays quiet, which is what keeps the thing
           * reading as interposed rather than as a bubble enclosing everything.
           */
          float facing = 0.28 + 0.72 * smoothstep(-0.45, 0.9, vNormal2.z);

          /* Nothing at the floor and nothing at the crown: both are closed curves. */
          float band = smoothstep(0.04, 0.5, height) * (1.0 - smoothstep(0.58, 1.0, height) * 0.65);

          /* A held object settles; it does not spin. One slow breath per plate. */
          float settle = 0.72 + 0.4 * sin(uTime * 0.85 + plateId * 2.2);

          float carry = plate * weight * facing * band * settle;
          float ripple = 0.72 + 0.34 * fbm(vGrid * 2.4 + vec2(0.0, -uTime * 0.5) + uSeed);

          /*
           * A ward against several materials is made of all of them: one per plate,
           * so "Reinforced Shielding" reads as seven things held at once instead of
           * as whichever violation happened to be its fixture's first row.
           */
          vec3 material = uColorA;
          if (uSpectrum > 0){
            int pick = int(mod(plateId, float(uSpectrum)));
            for (int i = 0; i < 7; i++){
              if (i == pick) material = uSpectrumColors[i];
            }
          }
          /*
           * A spectrum ward keeps its plates' own colours. The guard bias exists to
           * give an unattributed shield an identity; on a ward that already carries
           * seven, washing every plate toward the same pale blue erases exactly the
           * thing being said.
           */
          float wash = uSpectrum > 0 ? 0.28 : 0.42;
          float bias = uSpectrum > 0 ? 0.12 : 0.75;
          color = mix(material, emberRamp(0.55 + carry * 0.9), wash);
          color = mix(color, uIntentBias, uIntentAmount * bias);
          body = 0.02 + carry * 1.15 * ripple;
        }
        /* A dome has already been pulled toward its intent; twice is a grey shell. */
        if (uDome == 0) color = intentTint(color);
        float impulse = max(0.0, 1.0 - uBurst * 1.3);
        /*
         * The rim term is a function of latitude alone, so on a dome it is the base ellipse
         * wearing a different name — the plates carry the whole shell instead.
         */
        float edge = uDome == 1 ? 0.0 : rim * 0.34;
        float alpha = (edge + body * 0.4) * uActivation * (0.3 + 0.9 * uIntensity) * uAreaNorm;
        alpha *= 0.75 + 0.7 * impulse;
        alpha *= 1.0 - uDissolve;
        alpha *= clipped;
        /*
         * A dome meets the floor along a circle, and a rim term lights that circle
         * evenly — which draws a hard ring around the effect and gives the whole
         * thing away as geometry. Fading the volume out as it approaches the ground
         * lets it grow out of the surface instead of standing on it.
         */
        float floorFade = uDome == 1 ? 0.45 : 0.22;
        alpha *= smoothstep(0.0, floorFade, height) * (1.0 - smoothstep(0.72, 1.0, height) * 0.45);

        /* A ward is interposed, not opaque: whoever is inside it has to stay legible
           through it, or the shield has hidden the thing it is protecting. */
        gl_FragColor = vec4(color, clamp(alpha, 0.0, uDome == 1 ? 0.55 : 0.85));
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
    /* A shell is meant to be seen from inside as well as out. */
    side: DoubleSide,
  });

  material.blending = blendingFor(violation);
  return material;
}

export function createAmbientMaterial({ shared, violation, path = null, hint, params, motion, aim = false }) {
  const profile = violationProfile(violation, path);

  const material = new ShaderMaterial({
    uniforms: {
      ...shared,
      ...shapeUniforms(hint, params),
      ...emissionUniforms(profile),
      uMode: { value: profile.mode },
      uFlow: { value: hint === 'line' ? 1 : (hint === 'cone' ? 2 : 0) },
      /*
       * An aim's motes are the same cloud under a different law of motion. Slowed to
       * roughly a third, because the aim's whole job is to be holdable; and switched
       * from rising-and-scattering to GATHERING, which is the only motion that reads
       * as something about to happen rather than as something already burning.
       */
      uRate: { value: aim ? motion.rate * 0.34 : motion.rate },
      uRise: { value: motion.rise },
      uSwirl: { value: motion.swirl },
      uTwinkle: { value: motion.twinkle },
      uAim: { value: aim ? 1 : 0 },
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      ${LIFECYCLE_CHUNK}
      ${INTENT_CHUNK}
      ${IDEAL_SHAPE_CHUNK}
      attribute vec4 aRandom;
      uniform int uMode;
      uniform int uFlow;
      uniform float uRate;
      uniform float uRise;
      uniform float uSwirl;
      uniform float uAim;
      varying float vAlpha;
      varying float vTint;
      varying float vHot;

      void main(){
        float life = fract(aRandom.x + uTime * uRate * (0.6 + 0.8 * aRandom.y));
        vec3 p = gridPosition(position);
        vTint = aRandom.z;
        vHot = 1.0 - life;

        if (uFlow == 1){
          vec2 side = vec2(-uDirection.y, uDirection.x);
          p.xz = uOrigin + uDirection * (life * uLength) + side * ((aRandom.w - 0.5) * uHalfWidth * 1.6);
          p.y = 0.04 + aRandom.z * 0.45;
        }

        vec2 radial = p.xz - uOrigin;
        vec2 outward = radial / max(length(radial), 0.0001);

        /*
         * One motion, parameterised, instead of eight hand-written scatters.
         *
         * Rise carries it off the floor, swirl makes the rise wander — motes going
         * straight up read as a fountain, and nothing in this game is a fountain —
         * and the fade is a full breath in and out, so nothing ever pops on.
         */
        p.y += life * uRise * (0.7 + 0.6 * aRandom.y);
        p.xz += vec2(sin(uTime * (0.5 + aRandom.y) + aRandom.w * 6.28),
                     cos(uTime * (0.45 + aRandom.z) + aRandom.x * 6.28)) * uSwirl * life;
        if (uFlow == 2) p.xz += outward * life * uReach * 0.32;

        /*
         * Which way the action carries.
         *
         * This is the difference a player actually reads: motes rising and gathering
         * on someone is care, the same motes sinking and draining off them is harm.
         */
        if (uIntent != 0){
          p.y += uLift * life * (0.7 + 0.6 * aRandom.y);
          p.xz -= outward * uConverge * life * 0.55;
        }

        /*
         * AIMING: the gather, borrowed from radialMotes and folded into the loop.
         *
         * Its whole argument is that a mote pulled by linear drag covers exactly
         * v0/k before it stops, so aiming it at its own start distance lands it on
         * the target with its speed going to zero there. Here the closed-form
         * 1 - exp(-k*t) is applied to the mote's own offset from the aim point,
         * which is the same statement: each one leaves the ground it was born on,
         * eases inward along the line it started on, and settles at the centre
         * instead of shooting through it. Motes born across the footprint therefore
         * describe the footprint on their way in — a path, not a scatter.
         *
         * It hugs the floor. A rise that carries motes off the ground puts them over
         * cells the shot does not claim, and the one thing an aim must never do is
         * imply ground it will not touch.
         */
        if (uAim > 0.5){
          vec3 born = gridPosition(position);
          /*
           * Slack drag, and only half the distance travelled over a whole life.
           *
           * The first cut used the arrival's own numbers and they were wrong here for
           * a reason worth keeping: a mote is brightest in the middle of its life, and
           * at a drag of 3.1 it is four fifths of the way home by then — so every mote
           * on the board was bunched into the one cell under the pointer, where the
           * tint is already brightest and nothing could be seen. Eased over half the
           * distance instead, a mote spends its bright half still out over the ground
           * it was born on, and what the player watches is the whole footprint drawing
           * inward rather than a knot of light at the centre.
           */
          float settle = 1.0 - exp(-1.7 * life);
          p.xz = mix(born.xz, uOrigin, settle * 0.5);
          p.y = 0.02 + born.y * 0.35 + uRise * 0.12 * settle;
          p.xz += vec2(sin(uTime * 0.32 + aRandom.w * 6.28),
                       cos(uTime * 0.29 + aRandom.x * 6.28)) * uSwirl * 0.5;
        }

        vAlpha = sin(life * 3.14159) * uActivation * (0.3 + 0.8 * uIntensity) * (1.0 - uDissolve);
        /* An aim's cloud is held at a third of a cast's activation, and it is the only
           thing in the aim that moves — so it is given back some of what the phase
           takes, or the motes lose to the tint they are drifting over. */
        if (uAim > 0.5) vAlpha *= 1.7;
        gl_Position = gridToClip(p);
        float grain = uAim > 0.5 ? 0.7 : 1.0;
        gl_PointSize = clamp((0.035 + 0.05 * aRandom.y) * (1.0 - life * 0.5) * uCellPx * 1.7 * grain, 1.0, 26.0);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying float vAlpha;
      varying float vTint;
      varying float vHot;
      uniform int uMode;
      uniform float uTwinkle;
      uniform float uAim;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      ${INTENT_CHUNK}
      void main(){
        /*
         * A point sprite is a square unless its own falloff makes it round, and a
         * linear falloff still leaves a visible disc edge on a dark board. A tight
         * core inside a much wider bloom is what makes a mote read as light in air
         * rather than as a dot pasted over the floor.
         */
        vec2 q = gl_PointCoord - 0.5;
        float d = dot(q, q);
        float core = exp(-d * 60.0);
        float glow = exp(-d * 10.0);
        /* Nothing in an aim may flicker. A 9 Hz twinkle across two hundred motes is
           the single fastest thing on the board, and a player holding an aim while
           they choose a cell is looking straight at it. */
        float twinkle = mix(1.0, 0.35 + 0.65 * sin(uTime * 9.0 + vTint * 30.0), uTwinkle * (1.0 - uAim));
        vec3 color = intentTint(emberRamp(core * vHot * 1.5 + glow * 0.22));
        float a = (core * 1.2 + glow * 0.28) * vAlpha * twinkle * 0.8;
        if (a < 0.006) discard;
        /* A mote is nearly all core, so the shadow alone barely moves it — the sink
           toward the deep end has to reach the ramp's input as well, or the ground
           goes to shadow while the light above it stays lit. */
        gl_FragColor = vec4(shadowed(color, uColorA), clamp(a, 0.0, 1.0));
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
  });

  material.blending = blendingFor(violation);
  return material;
}

/**
 * The burst: the particles that ARE the impact.
 *
 * Everything the rejected version was missing is in this one material. The cloud is
 * born at the moment of arrival rather than looping in the background; every
 * particle carries a velocity inherited from the direction the attack came in on;
 * it decelerates against drag and falls under gravity; and it runs a size ramp and
 * a colour ramp over its own life, so it leaves as a cooling ember instead of
 * vanishing as a dimmed dot.
 *
 * The whole flight is solved in closed form from `uSince`, so this is one draw call
 * with nothing uploaded per frame — a directional burst costs what the old static
 * scatter cost.
 */
export function createBurstMaterial({ shared, violation, path = null, gravity = null }) {
  const profile = violationProfile(violation, path);
  const style = burstStyle(violation);

  const material = new ShaderMaterial({
    uniforms: {
      ...shared,
      ...emissionUniforms(profile),
      uDrag: { value: style.drag },
      uGravity: { value: gravity === null ? style.gravity : gravity },
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      ${LIFECYCLE_CHUNK}
      ${PARTICLE_PATH_CHUNK}
      attribute vec4 aBurst;
      attribute vec3 aVelocity;
      uniform float uDrag;
      uniform float uGravity;
      varying float vLife;
      varying float vRand;

      void main(){
        float age = uSince - aBurst.x;
        vLife = 1.0;
        vRand = aBurst.w;
        if (age < 0.0 || age > aBurst.y){
          gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
          gl_PointSize = 0.0;
          return;
        }
        vec3 p = dragPath(position, aVelocity, uDrag, uGravity, age);
        /* Debris settles onto the floor rather than sinking through the board; what
           is rising has no floor to settle onto. */
        if (uGravity > 0.0) p.y = max(p.y, 0.015);
        vLife = age / aBurst.y;

        /* Size over life: born small and violent, swelling as it cools and thins out. */
        float size = aBurst.z * mix(0.7, 1.5, vLife) * (1.0 - smoothstep(0.6, 1.0, vLife));
        gl_Position = gridToClip(p);
        gl_PointSize = clamp(size * uCellPx * (0.85 + 0.5 * uIntensity), 1.0, 32.0);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying float vLife;
      varying float vRand;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      ${INTENT_CHUNK}
      void main(){
        vec2 q = gl_PointCoord - 0.5;
        float d = dot(q, q);
        float core = exp(-d * 70.0);
        /*
         * The halo is kept deliberately faint. A spark is a POINT with a suggestion
         * of glow; give every sprite a generous bloom and fifty of them overlapping
         * stop being sparks and become one lit fog — which is what the rejected
         * version looked like from two cells away.
         */
        float glow = exp(-d * 13.0);

        /* Colour over life: white-hot, through the material's own colour, to ash. */
        float heat = (1.0 - vLife) * (1.0 - vLife);
        vec3 color = intentTint(emberRamp(core * heat * 1.7 + glow * heat * 0.3));

        float alpha = (core * 1.15 + glow * 0.16) * pow(1.0 - vLife, 1.4);
        /* Embers flicker as they tumble, and flicker harder as they cool. */
        alpha *= 1.0 - 0.35 * vLife * (0.5 + 0.5 * sin(uTime * 26.0 + vRand * 40.0));
        alpha *= uActivation * (0.45 + 0.7 * uIntensity) * (1.0 - uDissolve);
        if (alpha < 0.006) discard;
        gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
    blending: AdditiveBlending,
  });

  return material;
}

/**
 * Darkness, drawn as a material that takes light off the board rather than as a
 * lamp with negative intensity — which does not exist and, where engines fake it,
 * behaves like a hole in the depth buffer.
 */
export function createOcclusionMaterial({ shared }) {
  return new ShaderMaterial({
    uniforms: {
      ...shared,
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      varying vec2 vUvv;
      void main(){
        vUvv = uv;
        gl_Position = gridToClip(gridPosition(position));
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vUvv;
      ${LIFECYCLE_CHUNK}
      void main(){
        float d = length(vUvv - 0.5) * 2.0;
        float a = pow(max(0.0, 1.0 - d), 1.6) * 0.5 * uActivation * (0.4 + 0.8 * uIntensity) * (1.0 - uDissolve);
        gl_FragColor = vec4(0.01, 0.008, 0.03, a);
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
    blending: NormalBlending,
  });
}

/**
 * The solid parts some violations grow rather than emit: Cold's crystal front and
 * Light's ray field. Both are one merged buffer and one draw call — a shard per
 * cell would be per-cell emitters wearing a different hat.
 *
 * Each vertex carries the base it grows from and how far down the front it sits, so
 * the whole field sprouts outward from the origin in the vertex shader without a
 * per-object update on the CPU.
 */
export function createShardMaterial({ shared, violation, path = null, kind }) {
  const profile = violationProfile(violation, path);
  const material = new ShaderMaterial({
    uniforms: {
      ...shared,
      uKind: { value: kind === 'ray' ? 1 : 0 },
      ...emissionUniforms(profile),
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      ${LIFECYCLE_CHUNK}
      attribute vec3 aBase;
      attribute vec4 aShard;
      uniform int uKind;
      varying float vBody;
      varying vec2 vShape;
      void main(){
        float delay = aShard.x;
        float phase = aShard.y;
        float grow = uKind == 1
          ? 0.85 + 0.2 * sin(uTime * 1.1 + phase * 6.28)
          : clamp((uGrowth - delay * 0.8) * 2.4, 0.0, 1.0);
        grow *= 1.0 - uDissolve;
        vec3 local = aBase + (position - aBase) * max(grow, 0.0001);
        vShape = aShard.zw;
        vBody = grow;
        gl_Position = gridToClip(gridPosition(local));
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying float vBody;
      varying vec2 vShape;
      uniform int uKind;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      void main(){
        /*
         * A ray is a beam of lit air, not a card standing on the floor. Without a
         * falloff across its own width and up its length it renders as exactly what
         * it is made of — a rectangle hanging in the scene — so both edges are faded
         * out and it never shows a border.
         */
        float across = 1.0 - abs(vShape.x * 2.0 - 1.0);
        float along = vShape.y;
        float fade = uKind == 1
          ? pow(across, 1.8) * pow(max(0.0, 1.0 - along), 1.4)
          : mix(1.0, 0.55, along);
        vec3 color = uKind == 1
          ? emberRamp(0.55 + across * 0.5)
          : emberRamp(0.25 + 0.6 * vBody * (1.0 - along * 0.5) + pow(across, 6.0) * 0.4);
        float alpha = (uKind == 1 ? 0.5 : 0.6) * vBody * fade
          * uActivation * (0.4 + 0.8 * uIntensity) * uAreaNorm * (1.0 - uDissolve);
        if (alpha < 0.004) discard;
        gl_FragColor = vec4(color, alpha);
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
  });
  material.blending = kind === 'ray' ? AdditiveBlending : NormalBlending;
  return material;
}

/**
 * The moment of arrival, thrown onto the floor.
 *
 * This replaces the expanding torus that used to play here. A torus is a STROKE: a
 * ring of constant width scaled up over a second, which on a raked board projects to
 * a beige elliptical outline sitting on the ground — the single most artificial thing
 * in the whole renderer and the first thing the eye went to.
 *
 * What actually happens when something lands is that the ground under it is briefly
 * over-lit and the light runs outward and dies. So: a radial gradient with no
 * boundary anywhere in it, expanding on its own clock, gone in a fifth of a second.
 */
export function createFlashMaterial({ shared, violation, path = null }) {
  const profile = violationProfile(violation, path);
  return new ShaderMaterial({
    uniforms: {
      ...shared,
      ...emissionUniforms(profile),
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      varying vec2 vUvv;
      void main(){
        vUvv = uv;
        gl_Position = gridToClip(gridPosition(position));
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vUvv;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      ${NOISE_CHUNK}
      void main(){
        float d = length(vUvv - 0.5) * 2.0;
        if (d > 1.0) discard;
        /* The light runs outward as it dies, so the falloff loosens over time. */
        /*
         * Wide from the first frame. A pool that starts tight and grows is a BALL of
         * light hanging in the air for the first few frames — the eye reads a small
         * bright disc as an object, and a large soft one as illumination. It opens
         * further as it dies, which is what a shockfront does to lit dust.
         */
        /*
         * It opens as it dies, but not without limit: an uncapped spread eventually
         * fills the whole disc the geometry allows, so the pool's real extent came
         * from the quad rather than from the falloff and the effect lit ground it
         * had no business lighting a third of a second after it landed.
         */
        float spread = min(0.8, 0.5 + uSince * 0.9);
        float lit = exp(-pow(d / max(spread, 0.05), 2.0) * 2.2);
        /* Broken up, so the pool is a lit floor rather than a painted disc. */
        lit *= 0.7 + 0.6 * fbm(vUvv * 7.0 + uSeed);
        float decay = exp(-uSince * 5.0);
        float a = lit * decay * 0.85 * uActivation * (0.5 + 0.7 * uIntensity) * (1.0 - uDissolve);
        if (a < 0.008) discard;
        /* Held well below white: the pool keeps the violation's colour at its peak. */
        gl_FragColor = vec4(emberRamp(min(0.5, lit * 0.55 * decay) + 0.08), clamp(a, 0.0, 0.85));
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
    blending: AdditiveBlending,
  });
}

/**
 * The mark an action leaves on the ground.
 *
 * Attacks scar the floor and the scar is the whole statement. Everything else in
 * the game — a ward raised, a wound closed, a curse hung on someone — is an
 * ARRANGEMENT, something deliberately placed, and a smear of light says none of
 * that. A ring of worked geometry does: concentric bands, tick marks, and a turning
 * glyph course, which is also how the Runic school is described in the world rather
 * than a generic fantasy circle.
 *
 * Which way it turns is the sentence: a mend and a ward wind up and inward, an
 * affliction unwinds against them, a survey sweeps once and does not stay.
 */
export function createSigilMaterial({ shared, violation, path = null, intent, radius }) {
  const profile = violationProfile(violation, path);
  const material = new ShaderMaterial({
    uniforms: {
      ...shared,
      uRadius: { value: radius },
      ...emissionUniforms(profile),
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      varying vec2 vLocal;
      void main(){
        vLocal = position.xz;
        gl_Position = gridToClip(gridPosition(position));
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying vec2 vLocal;
      uniform float uRadius;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      ${INTENT_CHUNK}
      ${NOISE_CHUNK}

      float band(float value, float centre, float width){
        return smoothstep(width, 0.0, abs(value - centre));
      }

      void main(){
        float r = length(vLocal) / max(uRadius, 0.001);
        if (r > 1.05) discard;

        float angle = atan(vLocal.y, vLocal.x);
        float spin = uTime * (uIntent == 3 ? -0.35 : 0.28);
        float turned = angle + spin;

        /*
         * The rings the whole figure is built on — WIDE and soft.
         *
         * A twelve-thousandth-of-a-radius band is a hairline, and a hairline drawn
         * around a circle on a raked board is a stroked ellipse: the single thing the
         * player named as looking cheap. These are broad, blurred bands of light with
         * no defined edge anywhere in them, so the figure is read from its structure
         * rather than from its outline.
         */
        float around = fract(turned / 6.2831853);

        /*
         * Nothing here is allowed to close.
         *
         * Every band is thinned unevenly around its own circumference, so what is
         * drawn is a set of arcs rather than a circle. An unbroken band on a raked
         * board is an ellipse however soft its edges are, and softening was the only
         * defence the rings previously had.
         */
        float arcs = 0.28 + 0.72 * fbm(vec2(around * 7.0, floor(uSeed * 5.0) + 2.0));

        float rings = (band(r, 0.8, 0.12) * 0.22 + band(r, 0.42, 0.09) * 0.3) * arcs;

        float ink = rings;

        if (uIntent == 1){
          /*
           * A mend GATHERS, and the mark has to say so on its own.
           *
           * What stood here was a glyph course and six even spokes, which on a green
           * pool of light read as a sunburst — a radiating emblem, the opposite
           * statement, and stamped-on clip art besides. Radial structure is dropped
           * from the mend entirely and replaced by the motion the intent already
           * names: arcs closing inward on the subject, one behind the other, brighter
           * the nearer they get. Nothing radiates and nothing completes a circle.
           */
          float pull = fract(uTime * 0.36);
          float lead = band(r, 0.95 - pull * 0.66, 0.1) * (0.3 + 0.7 * pull);
          float trail = band(r, 0.95 - fract(pull + 0.5) * 0.66, 0.08) * 0.55;
          ink += (lead + trail) * arcs * 1.0;
        } else if (uIntent == 2){
          /*
           * A bolster is the mend read the other way. Something has been ADDED, so
           * the bands travel outward from the subject instead of closing on them —
           * which is what separates a buff from a heal when both carry violation
           * None and hue alone has to do all the work otherwise.
           */
          float push = fract(uTime * 0.36);
          float lead = band(r, 0.2 + push * 0.72, 0.11) * (1.0 - push * 0.55);
          float trail = band(r, 0.2 + fract(push + 0.5) * 0.72, 0.08) * 0.5;
          ink += (lead + trail) * arcs * 0.9;
        } else {
          /*
           * A ward is ARRANGED — placed, worked, braced — so it keeps a built
           * structure. It is jittered per stroke rather than laid out on an even
           * division: six exact spokes are a wheel, and a wheel is a drawing.
           */
          float slot = floor(around * 9.0);
          float stroke = hashNoise(vec2(slot, floor(uSeed * 7.0) + 1.0));
          float braces = band(fract(around * 9.0), 0.35 + stroke * 0.3, 0.04 + stroke * 0.05)
                       * smoothstep(0.38, 0.58, r) * smoothstep(0.94, 0.74, r)
                       * step(0.35, stroke);
          ink += braces * 0.16;
        }

        /*
         * The figure is lit INTO the floor, not printed on it. A pool of light under
         * the whole mark, and every stroke bloomed outward from itself, so what the
         * eye reads first is a lit patch of ground with structure in it rather than a
         * set of hairlines — which is what separates an inscribed circle from clip art.
         */
        float bloom = smoothstep(1.02, 0.0, r);
        float pool = pow(bloom, 2.2) * (0.5 + 0.4 * fbm(vLocal * 2.2 + uSeed));
        /*
         * The POOL is the mark, and the structure is detail inside it. Most of this
         * family lands on one figure whose plinth hides the floor directly under it,
         * so a mark carried by its strokes is a mark nobody sees — and the strokes
         * are also the part that risks looking drawn. Light on the ground is the one
         * ground indication the doctrine allows, so it is what does the work.
         */
        ink = ink * (0.4 + 0.7 * bloom) + pool * 1.45;

        /*
         * Growth. A mark is inscribed rather than switched on: it is drawn outward
         * from the middle as the action takes hold, and unwritten the same way.
         */
        float written = smoothstep(uGrowth * 1.15, uGrowth * 1.15 - 0.35, r);
        ink *= written;

        /*
         * The mark is where a supportive action states what it IS. Most of this
         * family carries violation None, or a material that says nothing about
         * purpose, so the intent's own colour is pushed harder here than anywhere
         * else in the effect — which is what lets a heal, a blessing and a ward be
         * told apart at a glance on the same board.
         */
        /*
         * Held below the emitting core on purpose. Run the ramp high and every mark
         * blows out to the same near-white centre, which is how a heal and a buff
         * ended up as the same green-white pool: the None violation they share was
         * deciding the colour and the intent was a tint on top of white.
         */
        vec3 color = mix(intentTint(emberRamp(0.3 + ink * 0.36)), uIntentBias, 0.24 + uIntentAmount * 0.5);
        float alpha = ink * uActivation * (0.45 + 0.75 * uIntensity) * (1.0 - uDissolve);
        alpha *= 1.05;
        if (alpha < 0.004) discard;
        gl_FragColor = vec4(color, clamp(alpha, 0.0, 0.85));
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
    blending: AdditiveBlending,
  });
  return material;
}

/**
 * The element of an aim, on its way.
 *
 * A firing line drawn as a stroke says only "from here to there". What a player
 * should be able to read off an armed aim, before they commit anything, is WHAT is
 * about to travel — and every violation moves differently enough that the motion
 * alone identifies it. So the line is gone and what crosses the gap is the material.
 *
 * But the material still has to state the SHOT. An arc says the thing lobs over the
 * ground between here and there, which is a claim about trajectory nobody made and
 * which no skill in the game resolves that way; and a wide fan of particles reads as
 * weather rather than as a line of fire. So the flight is dead straight and held
 * tight to the line: a beam with grain in it, the grain being the element.
 *
 *   Heat      coarse and restless, the widest of the tight set.
 *   Cold      slow and slightly loose, nothing about frost is eager.
 *   Energy    a fast, fine jitter across the line, arriving flat.
 *   Light     the tightest and the only one that does not wander at all.
 *   Darkness  heavy grain, carried by its rim rather than its body.
 *   Physical  dense and blunt.
 *   Mental    the loosest, and the slowest — it is not travelling THROUGH the space.
 *
 * `spread` is how far off the line a particle may sit — a fraction of a cell, so the
 * whole stream stays inside the width of the shot — `jitter` how fast it worries
 * about that place, `rate` how many trips it makes a second (the one number that must
 * stay slow, since this runs for as long as the player holds an aim), and `size` the
 * grain of the element.
 */
export const TRAIL_STYLES = {
  None: { spread: 0.045, jitter: 0.7, rate: 0.34, size: 1.0 },
  Physical: { spread: 0.05, jitter: 0.4, rate: 0.46, size: 0.85 },
  Mental: { spread: 0.075, jitter: 0.5, rate: 0.24, size: 1.15 },
  Energy: { spread: 0.04, jitter: 2.4, rate: 0.52, size: 0.85 },
  Heat: { spread: 0.06, jitter: 1.0, rate: 0.40, size: 1.05 },
  Cold: { spread: 0.065, jitter: 0.35, rate: 0.26, size: 1.0 },
  Light: { spread: 0.018, jitter: 0.0, rate: 0.5, size: 0.9 },
  Darkness: { spread: 0.055, jitter: 0.6, rate: 0.30, size: 1.25 },
};

/**
 * How high off the floor the aim's beam runs, in cells.
 *
 * The same height a real shot crosses at, so what the aim shows is where the trace
 * will be — and clear of the floor, since a line grazing the boards reads as paint
 * on the ground rather than as something in the air over it.
 */
export const AIM_BEAM_HEIGHT = 0.62;

/**
 * The flight length, in cells, at which a style's `rate` means what it says.
 *
 * `rate` is trips per second down whatever gap the aim happens to span, so without a
 * reference it is not a speed at all. Held at six cells — a typical mid-range shot on
 * this board — so the existing per-element rates keep the pace they were tuned at and
 * everything shorter or longer is scaled off that rather than re-timed.
 */
export const AIM_SPAN_REFERENCE = 6.0;

export function trailStyle(violation) {
  return TRAIL_STYLES[violation] || TRAIL_STYLES.None;
}

/**
 * @param from,to  grid-space {x,z} endpoints of the flight
 */
export function createTrailMaterial({ shared, violation, path = null, from, to, style }) {
  const profile = violationProfile(violation, path);

  const material = new ShaderMaterial({
    uniforms: {
      ...shared,
      ...emissionUniforms(profile),
      uFrom: { value: new Vector2(from.x, from.z) },
      uTo: { value: new Vector2(to.x, to.z) },
      uSpread: { value: style.spread },
      uJitter: { value: style.jitter },
      uRate: { value: style.rate },
      uGrain: { value: style.size },
      uDarkRim: { value: violation === 'Darkness' ? 1 : 0 },
    },
    vertexShader: /* glsl */`
      ${PROJECTION_CHUNK}
      ${LIFECYCLE_CHUNK}
      attribute vec4 aTrail;
      attribute float aSheath;
      uniform vec2 uFrom;
      uniform vec2 uTo;
      uniform float uSpread;
      uniform float uJitter;
      uniform float uRate;
      uniform float uGrain;
      varying float vRun;
      varying float vRand;
      varying float vSheath;

      void main(){
        /*
         * Two populations in one draw. The core is the beam; the sheath is the
         * material the beam is DISTURBING — finer, dimmer, and travelling at about a
         * third of the speed, so the eye reads a fast line inside a slow haze rather
         * than one stream at one velocity, which is what made the beam read as flat.
         */
        float sheath = aSheath;
        vSheath = sheath;

        vec2 line = uTo - uFrom;
        float span = max(length(line), 0.001);
        vec2 along = line / span;
        vec2 side = vec2(-along.y, along.x);

        /*
         * One clock, no state. Each particle's whole trip is a function of uTime and
         * its own four constants, so the trail costs one draw call with nothing
         * uploaded per frame and cannot drift out of step with anything else.
         *
         * The run is a FRACTION of the gap, so a rate applied to it directly is a
         * number of trips per second and not a speed: the same aim held at two cells
         * and at twelve crawled in one and tore across the other, and a diagonal —
         * longer in cells than the row and column it spans — ran faster than the
         * straight shot beside it at the same reach. Dividing by the real length in
         * cells makes uRate a speed in cells per second, so the material moves at its
         * own pace whatever the aim is pointed at. Clamped at the near end because
         * below about a cell and a half the trip is over faster than the eye resolves
         * it and the stream strobes.
         */
        float pace = uRate * mix(1.0, 0.3, sheath) * clamp(${AIM_SPAN_REFERENCE.toFixed(1)} / span, 0.35, 3.2);
        float run = fract(aTrail.x + uTime * pace * (0.8 + 0.4 * aTrail.y));
        vRun = run;
        vRand = aTrail.w;

        /* Held across the line by its own constant, not by the clock, and held to a
           fraction of a cell: the stream has grain, not width. The offset does NOT
           open out over the run — a cone says the shot scatters, and this one does
           not. */
        float width = uSpread * mix(1.0, 3.4, sheath);
        float off = (aTrail.z - 0.5) * 2.0 * width;
        /* And worrying about that place, at the element's own frequency — the sheath
           more loosely and more slowly than the beam it hangs around. */
        off += sin(uTime * uJitter * mix(2.2, 0.7, sheath) + aTrail.w * 6.28) * width * 0.45;

        /*
         * The whole flow bends. A perfectly straight stream is a ruler, and a ruler is
         * the diagram this was supposed to stop being — what is wanted is a line that
         * is CARRIED: it wanders a little over its length and keeps changing its mind
         * about where, slowly. Two sines beaten against each other, one in the run and
         * one in the clock, seeded per effect so no two aims wander alike, and never
         * closing on the ends — the muzzle and the target are fixed points and only
         * the middle of the flight is free to move.
         */
        float wander = sin(run * 2.7 + uTime * 0.55 + uSeed * 6.0) * 0.62
                     + sin(run * 5.3 - uTime * 0.37 + uSeed * 11.0) * 0.38;
        float held = sin(run * 3.14159);
        /* Longer flights are freer, but the bend is capped: an aim that snakes is an
           aim that no longer states which cells the shot crosses. */
        float amplitude = uSpread * mix(2.6, 5.0, sheath) * clamp(span * 0.16, 0.35, 1.15);
        off += wander * held * amplitude;

        vec2 ground = uFrom + line * run + side * off;

        /* Straight, and level. The beam is the line between caster and aim point and
           nothing bends it — the same height at both ends, with only the element's
           own grain around it. The sheath is scattered AROUND that line rather than
           along it, so the haze has a section and is not a second flat ribbon. */
        float rise = (aTrail.y - 0.5) * 2.0 * width * mix(0.7, 1.6, sheath);
        /* And it drifts in height the same way, on a different beat, so the wander is
           a bend through the air rather than a snake drawn on one plane. */
        rise += sin(run * 3.9 + uTime * 0.43 + uSeed * 4.0) * held * amplitude * 0.55;
        vec3 p = vec3(ground.x, ${AIM_BEAM_HEIGHT.toFixed(2)} + rise, ground.y);

        gl_Position = gridToClip(p);
        /* A plateau, not a bell. A sine over the run is a lens — brightest at the middle of
           the gap and gone at both ends, which is the shape of a lob, not of a line.
           The beam is even along its whole length and only eased in at the muzzle and
           out at the target so nothing pops on. */
        float presence = smoothstep(0.0, 0.10, run) * smoothstep(1.0, 0.86, run);
        float grain = uGrain * mix(1.0, 0.42, sheath);
        gl_PointSize = clamp((0.022 + 0.03 * aTrail.y) * grain * (0.55 + presence * 0.7) * uCellPx * 1.6, 1.0, 18.0);
      }
    `,
    fragmentShader: /* glsl */`
      precision highp float;
      varying float vRun;
      varying float vRand;
      varying float vSheath;
      uniform int uDarkRim;
      ${LIFECYCLE_CHUNK}
      ${EMISSION_CHUNK}
      ${INTENT_CHUNK}
      void main(){
        vec2 q = gl_PointCoord - 0.5;
        float d = dot(q, q);
        float core = exp(-d * 52.0);
        float glow = exp(-d * 9.0);
        /* In leaving the caster and out again at the target, so nothing pops on at
           either end of the flight — but level in between, so the run is a beam and
           not a bead sliding along one. */
        float presence = smoothstep(0.0, 0.10, vRun) * smoothstep(1.0, 0.86, vRun);
        vec3 color = intentTint(emberRamp(core * (0.5 + presence * 0.9) + glow * 0.2));
        float a = (core * 1.1 + glow * 0.3) * presence;
        /*
         * A dark element cannot be drawn by its body — its body is the colour of the
         * board. What carries it is the rim: the core is cut away and only the halo
         * is left, so the particle reads as a hole with an edge.
         */
        if (uDarkRim == 1){
          a = glow * 0.55 * presence;
          color = mix(color, uColorCore, 0.6);
        }
        /* The sheath is the beam's own light on what is around it, so it is dimmer
           than the beam by a lot — bright enough to be seen moving, never bright
           enough to compete with the line it surrounds. */
        a *= mix(1.0, 0.34, vSheath);
        a *= uActivation * (0.45 + 0.7 * uIntensity) * (1.0 - uDissolve);
        if (a < 0.006) discard;
        gl_FragColor = vec4(shadowed(color, uColorA), clamp(a, 0.0, 1.0));
      }
    `,
    transparent: true,
    depthWrite: false,
    ...projectionOptions(shared),
  });

  material.blending = blendingFor(violation);
  return material;
}
